/**
 * Collapsible section (.sec) — the unified expandable card pattern.
 *
 * Used for: review loops, BDD scenarios, learnings, config sections.
 * CSS: styles/components/section.css
 *
 * Wire after innerHTML: togSec() is exposed globally for onclick.
 */

import { esc } from '../../shared/rendering.js'
import { action } from './action-delegation.js'
import type { SectionProps } from '../../types/components.js'

/**
 * Extended props allowing raw HTML in title/badge for composite components
 * like reviewLoop that need styled content in the header.
 */
export interface SectionRenderOpts extends SectionProps {
  /** Raw HTML title — use instead of `title` when styled content is needed. */
  titleHtml?: string
  /** Raw HTML badge — use instead of `badge` when styled content is needed. */
  badgeHtml?: string
}

/** Render a collapsible section card. */
export function renderSection(props: SectionRenderOpts): string {
  const openClass = props.defaultOpen ? ' open' : ''
  const titleContent = props.titleHtml ?? esc(props.title)
  const badgeContent = props.badgeHtml ?? (props.badge ? esc(props.badge) : null)

  return `<div class="sec${openClass}">
    <div class="sec-h" ${action('togSec')}>
      <span class="sec-title">${titleContent}</span>
      ${badgeContent ? `<span class="sec-badge">${badgeContent}</span>` : ''}
      <span class="sec-chev">&#9654;</span>
    </div>
    <div class="sec-body">${props.content}</div>
  </div>`
}

/** Toggle open/close on a section. Exposed globally for onclick handlers. */
export function togSec(el: HTMLElement): void {
  el.closest('.sec')?.classList.toggle('open')
}
